import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { Copy, MessageCircle, QrCode, Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { waLink, priceLabel, http, errMsg } from "@/lib/api";
import { pixPayload, parseAmount } from "@/lib/pix";

const Step = ({ n, children }) => (
  <li className="flex gap-3 text-sm text-teal-100">
    <span className="font-mono-x flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#2DD4BF]/50 text-[11px] text-[#2DD4BF]">{n}</span>
    <span>{children}</span>
  </li>
);

const PixKey = ({ settings }) => {
  const copy = () => {
    navigator.clipboard?.writeText(settings.pix_key);
    toast.success("Chave Pix copiada!");
  };
  if (!settings?.pix_key) return <p className="text-sm text-teal-200" data-testid="checkout-pix-unavailable">A chave Pix será enviada pelo WhatsApp.</p>;
  return (
    <div className="rounded-md border border-[#163D43] bg-[#041214] p-4">
      <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-teal-300">Chave Pix · CNPJ</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="font-display break-all text-xl text-teal-50" data-testid="checkout-pix-key">{settings.pix_key}</span>
        <button onClick={copy} data-testid="checkout-pix-copy" className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#2DD4BF]/50 px-3 py-2 text-xs font-semibold text-[#2DD4BF] hover:bg-[#2DD4BF]/10">
          <Copy className="h-3.5 w-3.5" /> Copiar
        </button>
      </div>
      {settings.pix_holder && <p className="mt-2 text-xs text-teal-300">Titular: {settings.pix_holder}</p>}
    </div>
  );
};

const PixQr = ({ settings, price }) => {
  const payload = pixPayload({ key: settings?.pix_key, name: settings?.pix_holder, city: settings?.pix_city, amount: parseAmount(price) });
  if (!payload) return null;
  const copy = () => {
    navigator.clipboard?.writeText(payload);
    toast.success("Pix copia e cola copiado!");
  };
  return (
    <div className="flex flex-col items-center gap-3" data-testid="checkout-pix-qr">
      <div className="rounded-md bg-white p-3 shadow-[0_0_40px_-8px_rgba(45,212,191,0.5)]">
        <QRCodeSVG value={payload} size={176} level="M" fgColor="#041214" />
      </div>
      <p className="text-center text-xs text-teal-200">Aponte a câmera do app do banco — o valor já vem preenchido.</p>
      <button onClick={copy} data-testid="checkout-pix-copy-paste" className="inline-flex items-center gap-1.5 rounded-full border border-[#2DD4BF]/50 px-4 py-2 text-xs font-semibold text-[#2DD4BF] hover:bg-[#2DD4BF]/10">
        <Copy className="h-3.5 w-3.5" /> Copiar Pix copia e cola
      </button>
    </div>
  );
};

const inputCls = "border-[#163D43] bg-[#041214] text-teal-50";

const PurchaseForm = ({ item, kind, onCode }) => {
  const [form, setForm] = useState({ client_name: "", client_phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/purchase", { item_id: item.id, kind, ...form });
      onCode(data?.code || "");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="purchase-form">
      <Input required minLength={2} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Seu nome" className={inputCls} data-testid="purchase-name" />
      <Input required type="tel" minLength={10} value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="Seu WhatsApp com DDD" className={inputCls} data-testid="purchase-phone" />
      <button disabled={busy} data-testid="purchase-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2DD4BF] py-4 font-semibold text-[#041214] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />} Gerar código do pedido
      </button>
    </form>
  );
};

const CodeBox = ({ code, wa }) => {
  const copy = () => {
    navigator.clipboard?.writeText(code);
    toast.success("Código copiado!");
  };
  return (
    <div className="space-y-3" data-testid="purchase-code-box">
      <div className="glow-border rounded-[4px] p-px">
        <div className="rounded-[3px] bg-[#041214] p-4 text-center">
          <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-teal-300">Código do seu pedido</p>
          <button onClick={copy} className="font-display mt-1 inline-flex items-center gap-2 text-3xl font-semibold text-[#2DD4BF]" data-testid="purchase-code">{code} <Copy className="h-4 w-4" /></button>
          <p className="mt-1 text-xs text-teal-200">Guarde este código para acompanhar o pedido em tempo real.</p>
        </div>
      </div>
      <a href={wa} target="_blank" rel="noreferrer" data-testid="checkout-pix-whatsapp" className="flex items-center justify-center gap-2 rounded-full bg-[#2DD4BF] py-4 font-semibold text-[#041214] transition-transform hover:-translate-y-0.5">
        <MessageCircle className="h-4 w-4" /> Enviar comprovante no WhatsApp
      </a>
      <Link to={`/acompanhar/${code}`} className="block text-center text-sm text-teal-200 underline-offset-4 hover:text-[#2DD4BF] hover:underline" data-testid="purchase-track-link">Acompanhar meu pedido</Link>
    </div>
  );
};

export const CheckoutDialog = ({ item, settings, onClose, kind = "service" }) => {
  const name = item?.title || item?.name || "";
  const price = priceLabel(item);
  const [code, setCode] = useState("");
  const wa = waLink(settings?.whatsapp, `Olá! Fiz o Pix para: ${name} (${price}).${code ? ` Código do pedido: ${code}.` : ""} Segue o comprovante:`);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto border-[#163D43] bg-[#071D1F] text-teal-50" data-testid="checkout-dialog">
        <DialogHeader>
          <p className="font-mono-x flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-teal-300"><QrCode className="h-3.5 w-3.5" /> Pagamento via Pix</p>
          <DialogTitle className="font-display text-2xl font-semibold">{name}</DialogTitle>
          <DialogDescription className="text-teal-200" data-testid="checkout-price">Valor: <span className="text-lg text-[#2DD4BF]">{price}</span></DialogDescription>
        </DialogHeader>
        <PixQr settings={settings} price={price} />
        <PixKey settings={settings} />
        <ol className="space-y-3">
          <Step n="1">Escaneie o QR Code ou copie a chave Pix.</Step>
          <Step n="2">Confirme o pagamento de {price} no app do seu banco.</Step>
          <Step n="3">Informe seu nome e WhatsApp para gerar o código do pedido e envie o comprovante — o atendimento e a entrega continuam por lá.</Step>
        </ol>
        {code ? <CodeBox code={code} wa={wa} /> : <PurchaseForm item={item} kind={kind} onCode={setCode} />}
      </DialogContent>
    </Dialog>
  );
};
